import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { format } from 'date-fns';
import Breadcrumb from '../../components/Breadcrumbs/Breadcrumb';
import {
    fetchPayoutDetail,
    PayoutDetailResponse,
    StripeBalanceTransaction,
    triggerMipGiftSync,
    previewMipGiftSync,
    processPayoutSync,
    MipTransformResponse,
    fetchChargeMetadata,
    fetchPayoutChargeSyncs,
    PayoutChargeSyncsResponse,
    ChargeSyncRecord,
    reprocessCharge,
    previewChargeMipTransform,
    ChargeMipPreviewResponse,
} from '../../services/finance-services';
import { markRefundComplete } from '../../services/refund';
import { buildStripePaymentUrl, buildStripePayoutUrl } from '../../common/stripeUrl';
import { formatCalendarDate } from '../../common/calendarDate';
import { nxtFundUrl, nxtGiftUrl } from '../../common/nxtUrl';
import { getDonationDetail } from '../../services/campaign';
import DonationDetailDrawer, {
    DonationDetail,
} from '../../components/Contributions/drawers/DonationDetailDrawer';
import { toast } from 'react-hot-toast';

const statusColors: Record<string, string> = {
    paid: 'bg-success text-white',
    pending: 'bg-warning text-black',
    in_transit: 'bg-primary text-white',
    failed: 'bg-danger text-white',
    canceled: 'bg-meta-1 text-white',
};

const txnTypeColors: Record<string, string> = {
    charge: 'bg-success text-white',
    payment: 'bg-success text-white',
    refund: 'bg-danger text-white',
    payout: 'bg-primary text-white',
    adjustment: 'bg-warning text-black',
    stripe_fee: 'bg-gray-500 text-white',
    fee: 'bg-gray-500 text-white',
};

const mipStatusColors: Record<string, string> = {
    synced: 'bg-success text-white',
    pending: 'bg-warning text-black',
    processing: 'bg-primary text-white',
    failed: 'bg-danger text-white',
    'n/a': 'bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300',
    'not_processed': 'bg-gray-200 text-gray-600 dark:bg-gray-600 dark:text-gray-300',
};

const PayoutDetail = () => {
    const { payout_id } = useParams<{ payout_id: string }>();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState<PayoutDetailResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [loadingMore, setLoadingMore] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [syncError, setSyncError] = useState<string | null>(null);
    const [syncSuccess, setSyncSuccess] = useState<{ recordCount: number; cleanedUp: boolean } | null>(null);
    const [previewing, setPreviewing] = useState(false);
    const [previewData, setPreviewData] = useState<MipTransformResponse | null>(null);
    const [previewError, setPreviewError] = useState<string | null>(null);
    const [processing, setProcessing] = useState(false);
    const [processError, setProcessError] = useState<string | null>(null);
    const [processSuccess, setProcessSuccess] = useState<{ processed: number; failed: number } | null>(null);
    
    // State for charge metadata (keyed by charge ID)
    const [chargeMetadata, setChargeMetadata] = useState<Record<string, Record<string, string> | null>>({});
    const [loadingMetadata, setLoadingMetadata] = useState<Record<string, boolean>>({});
    
    // State for NXT charge sync records
    const [chargeSyncs, setChargeSyncs] = useState<PayoutChargeSyncsResponse | null>(null);
    const [loadingChargeSyncs, setLoadingChargeSyncs] = useState(false);
    
    // State for reprocessing individual charges
    const [reprocessingCharge, setReprocessingCharge] = useState<string | null>(null);
    const [reprocessError, setReprocessError] = useState<string | null>(null);

    // State for marking a refund complete (RE + MIP hand-entry), keyed by refund id
    const [markingRefund, setMarkingRefund] = useState<number | null>(null);

    // State for per-charge MIP preview
    const [chargeMipPreview, setChargeMipPreview] = useState<ChargeMipPreviewResponse | null>(null);
    const [previewingChargeId, setPreviewingChargeId] = useState<string | null>(null);

    // State for MIP error details modal
    const [errorDetailsData, setErrorDetailsData] = useState<MipTransformResponse | null>(null);
    const [errorDetailsLoading, setErrorDetailsLoading] = useState(false);
    const [errorDetailsError, setErrorDetailsError] = useState<string | null>(null);

    // Donation Detail drawer, opened from a gift row in the NXT Gifts table.
    const [activeDonation, setActiveDonation] = useState<DonationDetail | null>(null);
    const [openingLocId, setOpeningLocId] = useState<number | null>(null);

    const openDonationDrawer = async (locId: number) => {
        setOpeningLocId(locId);
        try {
            const detail = await getDonationDetail(locId);
            setActiveDonation(detail);
        } catch {
            toast.error('Could not load donation');
        } finally {
            setOpeningLocId(null);
        }
    };

    const loadPayoutDetail = async (cursor?: string, append = false) => {
        if (!payout_id) return;

        try {
            if (append) {
                setLoadingMore(true);
            } else {
                setLoading(true);
            }
            setError(null);

            const params: { limit?: number; starting_after?: string } = {
                limit: 100,
            };
            if (cursor) params.starting_after = cursor;

            const response = await fetchPayoutDetail(payout_id, params);

            if (append && data) {
                // Totals already cover the whole payout, so only the rows accumulate.
                setData({
                    ...response,
                    transactions: [...data.transactions, ...response.transactions],
                });
            } else {
                setData(response);
            }
        } catch (e) {
            console.error('Error fetching payout detail:', e);
            setError(e instanceof Error ? e.message : 'Failed to fetch payout details');
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    };

    useEffect(() => {
        loadPayoutDetail();
    }, [payout_id]);

    // Load NXT charge sync records when payout has sync record
    const loadChargeSyncs = async () => {
        if (!payout_id) return;
        
        setLoadingChargeSyncs(true);
        try {
            const result = await fetchPayoutChargeSyncs(payout_id);
            setChargeSyncs(result);
        } catch (e) {
            console.error('Error fetching charge syncs:', e);
        } finally {
            setLoadingChargeSyncs(false);
        }
    };

    const handleReprocessCharge = async (chargeId: string, force: boolean = false) => {
        setReprocessingCharge(chargeId);
        setReprocessError(null);
        
        try {
            const result = await reprocessCharge(chargeId, force);
            
            if (result.success) {
                // Reload the charge syncs to show updated data
                await loadChargeSyncs();
            } else if (result.error?.includes('lookup ID') && !force) {
                // If it failed due to lookup ID conflict, offer to retry with force
                if (window.confirm(
                    `The original gift could not be deleted because it wasn't found. ` +
                    `Would you like to create a new gift with a unique ID instead?\n\n` +
                    `Error: ${result.error}`
                )) {
                    await handleReprocessCharge(chargeId, true);
                    return;
                }
            } else {
                setReprocessError(result.error || 'Unknown error');
            }
        } catch (e: any) {
            console.error('Error reprocessing charge:', e);
            setReprocessError(e.message || 'Failed to reprocess charge');
        } finally {
            setReprocessingCharge(null);
        }
    };

    useEffect(() => {
        // Load charge syncs when payout data is available and has sync record
        if (data?.payout?.has_sync_record) {
            loadChargeSyncs();
        }
    }, [data?.payout?.has_sync_record, payout_id]);

    const handleLoadMore = () => {
        if (data?.next_cursor && !loadingMore) {
            loadPayoutDetail(data.next_cursor, true);
        }
    };

    // Refunds aren't auto-linked to RE/MIP, so finance staff mark them complete
    // manually once entered into both systems (HHLTB-2586).
    const handleMarkRefundComplete = async (
        refundId: number,
        newStatus: 'complete' | 'pending'
    ) => {
        setMarkingRefund(refundId);
        try {
            await markRefundComplete(refundId, newStatus);
            await loadPayoutDetail();
            toast.success(
                newStatus === 'complete'
                    ? 'Refund marked complete'
                    : 'Refund reopened'
            );
        } catch (e) {
            toast.error(
                e instanceof Error ? e.message : 'Failed to update refund status'
            );
        } finally {
            setMarkingRefund(null);
        }
    };

    const formatCurrency = (amount: number, currency: string = 'USD') => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: currency,
        }).format(amount);
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return '';
        try {
            return format(new Date(dateString), 'MMM d, yyyy');
        } catch {
            return dateString;
        }
    };

    const formatDateTime = (dateString: string | null) => {
        if (!dateString) return '';
        try {
            return format(new Date(dateString), 'MMM d, yyyy h:mm a');
        } catch {
            return dateString;
        }
    };

    const handleMipSync = async () => {
        if (!payout_id || !data) return;

        setSyncing(true);
        setSyncError(null);
        setSyncSuccess(null);

        try {
            const result = await triggerMipGiftSync(payout_id);

            // Reload the payout detail to pick up the persisted status,
            // error message, and any per-document errors.
            await loadPayoutDetail();

            // Auto-delete mode posts and then removes everything, leaving the
            // payout pending rather than synced.
            const cleanedUp = Boolean(data.mip_always_delete) && result.mip_sync_status === 'pending';
            const documentsFailed = result.mip_failed_documents?.length ?? 0;

            if (
                result.mip_sync_status === 'failed' ||
                documentsFailed > 0 ||
                (!cleanedUp && result.mip_error_message)
            ) {
                setSyncError(
                    result.mip_error_message ||
                    `${documentsFailed} document${documentsFailed === 1 ? '' : 's'} rejected by MIP`
                );
            } else {
                setSyncSuccess({
                    recordCount: result.mip_records_created,
                    cleanedUp,
                });
                setTimeout(() => setSyncSuccess(null), 5000);
            }
        } catch (e) {
            setSyncError(e instanceof Error ? e.message : 'Failed to sync to MIP');
        } finally {
            setSyncing(false);
        }
    };

    const handlePreview = async () => {
        if (!payout_id) return;

        setPreviewing(true);
        setPreviewError(null);
        setPreviewData(null);

        try {
            const result = await previewMipGiftSync(payout_id);
            setPreviewData(result);
        } catch (e) {
            setPreviewError(e instanceof Error ? e.message : 'Failed to preview MIP transformation');
        } finally {
            setPreviewing(false);
        }
    };

    const closePreview = () => {
        setPreviewData(null);
        setPreviewError(null);
    };

    const handleProcessPayout = async (resetSkipped = false) => {
        if (!payout_id) return;

        setProcessing(true);
        setProcessError(null);
        setProcessSuccess(null);

        try {
            const result = await processPayoutSync(payout_id, { reset_skipped: resetSkipped });

            if (result.success && result.processed > 0) {
                setProcessSuccess({ processed: result.processed, failed: result.failed });
                
                // Reload the payout detail and charge syncs to get updated status
                await loadPayoutDetail();
                await loadChargeSyncs();
                
                // Clear success message after 5 seconds
                setTimeout(() => setProcessSuccess(null), 5000);
            } else if (result.skipped > 0 || result.failed > 0) {
                // Show detailed error from the first error
                const errorDetails = result.errors?.length > 0 
                    ? result.errors.map(e => e.error || e.charge_id).join('; ')
                    : `${result.skipped} skipped, ${result.failed} failed`;
                setProcessError(errorDetails);
                
                // Still reload charge syncs to show updated status
                await loadChargeSyncs();
                
                // Still reload to show updated status
                await loadPayoutDetail();
            } else {
                setProcessError('No charges were processed');
            }
        } catch (e) {
            setProcessError(e instanceof Error ? e.message : 'Failed to process payout');
        } finally {
            setProcessing(false);
        }
    };

    const handleFetchMetadata = async (chargeId: string) => {
        if (!chargeId || loadingMetadata[chargeId]) return;

        setLoadingMetadata((prev) => ({ ...prev, [chargeId]: true }));

        try {
            const result = await fetchChargeMetadata(chargeId);
            setChargeMetadata((prev) => ({ ...prev, [chargeId]: result.metadata }));
        } catch (e) {
            console.error('Error fetching charge metadata:', e);
            // Set to empty object to indicate we tried but failed
            setChargeMetadata((prev) => ({ ...prev, [chargeId]: null }));
        } finally {
            setLoadingMetadata((prev) => ({ ...prev, [chargeId]: false }));
        }
    };

    const handleChargePreview = async (chargeId: string) => {
        setPreviewingChargeId(chargeId);
        setChargeMipPreview(null);

        try {
            const result = await previewChargeMipTransform(chargeId);
            setChargeMipPreview(result);
        } catch (e) {
            setChargeMipPreview({
                success: false,
                charge_id: chargeId,
                records: [],
                total_records: 0,
                errors: [e instanceof Error ? e.message : 'Failed to preview'],
            });
        } finally {
            setPreviewingChargeId(null);
        }
    };

    const handleViewErrorDetails = async () => {
        if (!payout_id) return;

        setErrorDetailsLoading(true);
        setErrorDetailsError(null);
        setErrorDetailsData(null);

        try {
            const result = await previewMipGiftSync(payout_id);
            setErrorDetailsData(result);
        } catch (e) {
            setErrorDetailsError(e instanceof Error ? e.message : 'Failed to load MIP records');
        } finally {
            setErrorDetailsLoading(false);
        }
    };

    const closeErrorDetails = () => {
        setErrorDetailsData(null);
        setErrorDetailsError(null);
    };

    const closeChargePreview = () => {
        setChargeMipPreview(null);
    };

    const downloadCsv = (records: MipTransformResponse['records'], filename: string) => {
        const headers = [
            'Date', 'Batch ID', 'Doc #', 'Fund ID', 'Gift Code', 'Type',
            'GL Code', 'Cost Center', 'Program Code', 'FAS117', 'Region',
            'Department', 'Debit', 'Credit', 'Charge ID',
        ];

        const rows = records.map((r) => [
            r.date, r.batch_id, r.doc_number, r.fund_id, r.gift_code,
            r.transaction_type, r.gl_code, r.cost_center, r.program_code,
            r.fas117, r.region, r.department, r.debit_amount, r.credit_amount,
            r.charge_id,
        ]);

        const csvContent = [
            headers.join(','),
            ...rows.map((row) =>
                row.map((val) => {
                    const str = String(val ?? '');
                    return str.includes(',') || str.includes('"') || str.includes('\n')
                        ? `"${str.replace(/"/g, '""')}"`
                        : str;
                }).join(',')
            ),
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        link.click();
        URL.revokeObjectURL(url);
    };

    if (loading) {
        return (
            <>
                <Breadcrumb pageName='Payout Details' />
                <div className='flex items-center justify-center py-20'>
                    <div className='h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent'></div>
                </div>
            </>
        );
    }

    if (error || !data) {
        return (
            <>
                <Breadcrumb pageName='Payout Details' />
                <div className='rounded-sm border border-danger bg-danger bg-opacity-10 px-4 py-6 text-center text-danger'>
                    {error || 'Failed to load payout details'}
                </div>
                <div className='mt-4'>
                    <Link
                        to='/payouts'
                        className='inline-flex items-center gap-2 text-primary hover:underline'
                    >
                        ← Back to Payouts
                    </Link>
                </div>
            </>
        );
    }

    const { payout, transactions, totals, has_more, manual_payout_notice, auto_debit_notice, is_local_record, local_record_notice, mip_always_delete } = data;
    const stripePayoutUrl = buildStripePayoutUrl(payout.id);
    const hasPersistedMipErrors = Boolean(
        payout.mip_error_message?.trim() || payout.mip_failed_documents?.length
    );
    const effectiveMipSyncStatus =
        payout.mip_sync_status === 'synced' && hasPersistedMipErrors
            ? 'failed'
            : payout.mip_sync_status;
    const canAttemptMipSync =
        payout.has_sync_record &&
        payout.mip_sync_status !== 'n/a' &&
        (payout.charges_processed === undefined || payout.charges_processed > 0);

    return (
        <>
            <Breadcrumb pageName='Payout Details' />

            {/* Back link */}
            <div className='mb-6'>
                <Link
                    to='/payouts'
                    className='inline-flex items-center gap-2 text-primary hover:underline'
                >
                    ← Back to Payouts
                </Link>
            </div>

            {/* MIP Always-Delete Mode Notice */}
            {mip_always_delete && (
                <div className='mb-6 rounded-sm border border-warning bg-warning bg-opacity-10 px-4 py-3'>
                    <div className='flex items-center gap-2'>
                        <span className='inline-block rounded bg-warning px-2 py-0.5 text-xs font-medium text-black'>
                            MIP Auto-Delete ON
                        </span>
                        <span className='text-sm text-warning'>
                            <code>MIP_JV_ALWAYS_DELETE</code> is enabled — every MIP sync attempt deletes its session and documents afterward. Payouts remain at <code>pending</code> even on successful syncs.
                        </span>
                    </div>
                </div>
            )}

            {/* Test Payout Notice */}
            {is_local_record && (
                <div className='mb-6 rounded-sm border border-meta-8 bg-meta-8 bg-opacity-10 px-4 py-3'>
                    <div className='flex items-center gap-2'>
                        <span className='inline-block rounded bg-meta-8 px-2 py-0.5 text-xs font-medium text-white'>
                            Test Payout
                        </span>
                        <span className='text-sm text-meta-8'>
                            {local_record_notice || 'This is a test payout from local records.'}
                        </span>
                    </div>
                </div>
            )}

            {/* Payout Summary Card */}
            <div className='mb-6 rounded-sm border border-stroke bg-white p-6 shadow-default dark:border-strokedark dark:bg-boxdark'>
                <div className='flex flex-wrap items-start justify-between gap-4'>
                    <div>
                        <h2 className='mb-2 flex items-center gap-2 text-xl font-semibold text-black dark:text-white'>
                            Payout {payout.id}
                            {payout.is_test_payout && (
                                <span className='inline-block rounded bg-meta-8 px-2 py-0.5 text-xs font-medium text-white'>
                                    Test
                                </span>
                            )}
                        </h2>
                        {!payout.is_test_payout && stripePayoutUrl && (
                            <a
                                href={stripePayoutUrl}
                                target='_blank'
                                rel='noopener noreferrer'
                                className='mb-2 inline-block text-sm text-primary hover:underline'
                            >
                                View in Stripe
                            </a>
                        )}
                        <div className='flex flex-wrap items-center gap-3'>
                            <span
                                className={`inline-block rounded-full px-3 py-1 text-xs font-medium capitalize ${
                                    statusColors[payout.status] || 'bg-gray-200 text-gray-700'
                                }`}
                            >
                                {payout.status.replace('_', ' ')}
                            </span>
                            <span
                                className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${
                                    payout.automatic
                                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300'
                                        : 'bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300'
                                }`}
                            >
                                {payout.automatic ? 'Scheduled' : 'Manual'}
                            </span>
                        </div>
                    </div>
                    <div className='text-right'>
                        <div className='text-3xl font-bold text-black dark:text-white'>
                            {formatCurrency(payout.amount, payout.currency)}
                        </div>
                        <div className='text-sm text-gray-500'>
                            {payout.arrival_date &&
                                `Arrival: ${formatCalendarDate(payout.arrival_date)}`}
                        </div>
                    </div>
                </div>

                <div className='mt-6 grid grid-cols-2 gap-4 border-t border-stroke pt-4 dark:border-strokedark sm:grid-cols-4'>
                    <div>
                        <div className='text-sm text-gray-500'>Created</div>
                        <div className='font-medium'>{formatDateTime(payout.created)}</div>
                    </div>
                    <div>
                        <div className='text-sm text-gray-500'>Method</div>
                        <div className='font-medium capitalize'>{payout.method}</div>
                    </div>
                    <div>
                        <div className='text-sm text-gray-500'>Source Type</div>
                        <div className='font-medium capitalize'>{payout.source_type}</div>
                    </div>
                    <div>
                        <div className='text-sm text-gray-500'>Currency</div>
                        <div className='font-medium'>{payout.currency}</div>
                    </div>
                </div>

                {/* MIP Sync Section - Only for automatic payouts */}
                {payout.automatic && (
                    <div className='mt-6 flex flex-wrap items-center justify-between gap-4 border-t border-stroke pt-4 dark:border-strokedark'>
                        <div className='flex items-center gap-4'>
                            <div className='text-sm text-gray-500'>MIP Sync Status:</div>
                            <span
                                className={`inline-block rounded-full px-3 py-1 text-xs font-medium capitalize ${
                                    mipStatusColors[effectiveMipSyncStatus] ||
                                    'bg-gray-200 text-gray-600'
                                }`}
                            >
                                {effectiveMipSyncStatus === 'n/a'
                                    ? 'N/A'
                                    : effectiveMipSyncStatus === 'not_processed'
                                    ? 'Not Processed'
                                    : effectiveMipSyncStatus}
                            </span>
                            {payout.mip_sync_status === 'synced' && !hasPersistedMipErrors && payout.mip_records_created > 0 && (
                                <span className='text-sm text-gray-500'>
                                    ({payout.mip_records_created} records)
                                </span>
                            )}
                            {payout.mip_sync_status === 'synced' && !hasPersistedMipErrors && payout.mip_synced_at && (
                                <span className='text-sm text-gray-500'>
                                    Synced {formatDateTime(payout.mip_synced_at)}
                                </span>
                            )}
                        </div>
                        <div className='flex items-center gap-2'>
                            {/* Process button for payouts not yet processed - Step 1: Process charges to NXT */}
                            {!payout.has_sync_record && (
                                <button
                                    onClick={() => handleProcessPayout(false)}
                                    disabled={processing}
                                    className='inline-flex items-center justify-center gap-2 rounded border border-primary px-4 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary hover:text-white disabled:opacity-50'
                                >
                                    {processing ? (
                                        <>
                                            <div className='h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent'></div>
                                            Processing to NXT...
                                        </>
                                    ) : (
                                        'Process to NXT'
                                    )}
                                </button>
                            )}
                            {/* Reprocess button for payouts with skipped/failed charges - Step 1: Reprocess charges to NXT */}
                            {/* Show when: sync record exists AND (not synced to MIP OR has unprocessed charges) */}
                            {payout.has_sync_record && 
                                payout.mip_sync_status !== 'synced' && 
                                payout.mip_sync_status !== 'processing' && 
                                (payout.mip_sync_status !== 'pending' || 
                                 (payout.charges_total !== undefined && 
                                  payout.charges_processed !== undefined && 
                                  payout.charges_processed < payout.charges_total)) && (
                                <button
                                    onClick={() => handleProcessPayout(true)}
                                    disabled={processing}
                                    className='inline-flex items-center justify-center gap-2 rounded border border-warning px-4 py-2 text-sm font-medium text-warning transition-colors hover:bg-warning hover:text-white disabled:opacity-50'
                                >
                                    {processing ? (
                                        <>
                                            <div className='h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent'></div>
                                            Reprocessing to NXT...
                                        </>
                                    ) : (
                                        'Reprocess to NXT (Reset Skipped)'
                                    )}
                                </button>
                            )}
                            {/* Step 2: Preview/Sync buttons for processed payouts - only show when some charges were actually processed to NXT */}
                            {canAttemptMipSync && (
                                    <>
                                        <button
                                            onClick={handlePreview}
                                            disabled={previewing || syncing}
                                            className='inline-flex items-center justify-center gap-2 rounded border border-stroke px-4 py-2 text-sm font-medium text-black transition-colors hover:border-primary hover:text-primary disabled:opacity-50 dark:border-strokedark dark:text-white dark:hover:border-primary dark:hover:text-primary'
                                        >
                                            {previewing ? (
                                                <>
                                                    <div className='h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent'></div>
                                                    Loading...
                                                </>
                                            ) : (
                                                'Preview MIP Sync'
                                            )}
                                        </button>
                                        <button
                                            onClick={handleMipSync}
                                            disabled={syncing || previewing || payout.mip_sync_status === 'processing'}
                                            className='inline-flex items-center justify-center gap-2 rounded border border-primary px-4 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary hover:text-white disabled:opacity-50'
                                        >
                                            {syncing ? (
                                                <>
                                                    <div className='h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent'></div>
                                                    Syncing to MIP...
                                                </>
                                            ) : (
                                                payout.mip_sync_status === 'synced' ? 'Re-sync to MIP' : 'Sync to MIP'
                                            )}
                                        </button>
                                    </>
                                )}
                        </div>
                    </div>
                )}
            </div>

            {/* Sync Error Alert */}
            {syncError && (
                <div className='mb-6 rounded-sm border border-danger bg-danger bg-opacity-10 px-4 py-3 text-danger'>
                    MIP Sync failed: {syncError}
                </div>
            )}

            {/* Sync Success Alert */}
            {syncSuccess && (
                syncSuccess.cleanedUp ? (
                    <div className='mb-6 rounded-sm border border-stroke bg-gray-50 px-4 py-3 text-gray-700 dark:border-strokedark dark:bg-meta-4 dark:text-gray-300'>
                        MIP sync attempted — {syncSuccess.recordCount} records built and then deleted (always-delete mode).
                    </div>
                ) : (
                    <div className='mb-6 rounded-sm border border-success bg-success bg-opacity-10 px-4 py-3 text-success'>
                        Successfully synced {syncSuccess.recordCount} records to MIP
                    </div>
                )
            )}

            {/* Process Error Alert */}
            {processError && (
                <div className='mb-6 rounded-sm border border-danger bg-danger bg-opacity-10 px-4 py-3 text-danger'>
                    Processing failed: {processError}
                </div>
            )}

            {/* Process Success Alert */}
            {processSuccess && (
                <div className='mb-6 rounded-sm border border-success bg-success bg-opacity-10 px-4 py-3 text-success'>
                    Payout processed successfully! {processSuccess.processed} charges processed
                    {processSuccess.failed > 0 && `, ${processSuccess.failed} failed`}
                </div>
            )}

            {/* Preview Error Alert */}
            {previewError && (
                <div className='mb-6 rounded-sm border border-danger bg-danger bg-opacity-10 px-4 py-3 text-danger'>
                    Preview failed: {previewError}
                </div>
            )}

            {/* MIP Preview Modal */}
            {previewData && (
                <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4'>
                    <div className='max-h-[90vh] w-full max-w-6xl overflow-hidden rounded-sm bg-white shadow-lg dark:bg-boxdark'>
                        {/* Modal Header */}
                        <div className='flex items-center justify-between border-b border-stroke px-6 py-4 dark:border-strokedark'>
                            <div>
                                <h3 className='text-lg font-semibold text-black dark:text-white'>
                                    MIP Sync Preview
                                </h3>
                                <p className='text-sm text-gray-500'>
                                    {previewData.total_records} records will be created •{' '}
                                    {previewData.charges_processed} charges processed •{' '}
                                    {previewData.charges_skipped} skipped •{' '}
                                    {previewData.charges_failed} failed
                                </p>
                            </div>
                            <button
                                onClick={closePreview}
                                className='rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-meta-4 dark:hover:text-white'
                            >
                                <svg className='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                                </svg>
                            </button>
                        </div>

                        {/* Modal Body */}
                        <div className='max-h-[calc(90vh-140px)] overflow-auto p-6'>
                            {previewData.records.length === 0 ? (
                                <div className='py-10 text-center text-gray-500'>
                                    No records to sync.
                                </div>
                            ) : (
                                <div className='overflow-x-auto'>
                                    <table className='w-full table-auto text-sm'>
                                        <thead>
                                            <tr className='border-b border-stroke bg-gray-50 dark:border-strokedark dark:bg-meta-4'>
                                                <th className='px-3 py-2 text-left font-medium'>Date</th>
                                                <th className='px-3 py-2 text-left font-medium'>Batch ID</th>
                                                <th className='px-3 py-2 text-left font-medium'>Doc #</th>
                                                <th className='px-3 py-2 text-left font-medium'>Fund ID</th>
                                                <th className='px-3 py-2 text-left font-medium'>Gift Code</th>
                                                <th className='px-3 py-2 text-left font-medium'>Type</th>
                                                <th className='px-3 py-2 text-left font-medium'>GL Code</th>
                                                <th className='px-3 py-2 text-left font-medium'>Cost Center</th>
                                                <th className='px-3 py-2 text-left font-medium'>Program</th>
                                                <th className='px-3 py-2 text-left font-medium'>FAS117</th>
                                                <th className='px-3 py-2 text-left font-medium'>Region</th>
                                                <th className='px-3 py-2 text-left font-medium'>Dept</th>
                                                <th className='px-3 py-2 text-right font-medium'>Debit</th>
                                                <th className='px-3 py-2 text-right font-medium'>Credit</th>
                                                <th className='px-3 py-2 text-left font-medium'>Charge ID</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {previewData.records.map((record, idx) => (
                                                <tr
                                                    key={idx}
                                                    className='border-b border-stroke dark:border-strokedark'
                                                >
                                                    <td className='px-3 py-2 whitespace-nowrap'>{record.date}</td>
                                                    <td className='px-3 py-2'>
                                                        <code className='text-xs'>{record.batch_id}</code>
                                                    </td>
                                                    <td className='px-3 py-2'>{record.doc_number}</td>
                                                    <td className='px-3 py-2'>{record.fund_id}</td>
                                                    <td className='px-3 py-2'>{record.gift_code}</td>
                                                    <td className='px-3 py-2'>{record.transaction_type}</td>
                                                    <td className='px-3 py-2'>{record.gl_code}</td>
                                                    <td className='px-3 py-2'>{record.cost_center}</td>
                                                    <td className='px-3 py-2'>{record.program_code}</td>
                                                    <td className='px-3 py-2'>{record.fas117}</td>
                                                    <td className='px-3 py-2'>{record.region}</td>
                                                    <td className='px-3 py-2'>{record.department}</td>
                                                    <td className='px-3 py-2 text-right font-mono'>
                                                        {record.debit_amount || ''}
                                                    </td>
                                                    <td className='px-3 py-2 text-right font-mono'>
                                                        {record.credit_amount || ''}
                                                    </td>
                                                    <td className='px-3 py-2'>
                                                        <code className='text-xs'>{record.charge_id}</code>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}

                            {previewData.errors && previewData.errors.length > 0 && (
                                <div className='mt-4 rounded border border-danger bg-danger bg-opacity-10 p-4'>
                                    <div className='font-medium text-danger'>Errors:</div>
                                    <ul className='mt-2 list-disc pl-5 text-sm text-danger'>
                                        {previewData.errors.map((err, idx) => (
                                            <li key={idx}>{err}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>

                        {/* Modal Footer */}
                        <div className='flex items-center justify-end gap-3 border-t border-stroke px-6 py-4 dark:border-strokedark'>
                            {previewData.records.length > 0 && (
                                <button
                                    onClick={() => downloadCsv(
                                        previewData.records,
                                        `mip-preview-${payout_id}.csv`
                                    )}
                                    className='inline-flex items-center gap-2 rounded border border-stroke px-4 py-2 text-sm font-medium transition-colors hover:border-primary hover:text-primary dark:border-strokedark'
                                >
                                    Download CSV
                                </button>
                            )}
                            <button
                                onClick={closePreview}
                                className='rounded border border-stroke px-4 py-2 text-sm font-medium transition-colors hover:border-primary hover:text-primary dark:border-strokedark'
                            >
                                Close
                            </button>
                            <button
                                onClick={() => {
                                    closePreview();
                                    handleMipSync();
                                }}
                                disabled={
                                    syncing ||
                                    previewData.total_records === 0 ||
                                    payout.mip_sync_status === 'processing'
                                }
                                className='inline-flex items-center justify-center gap-2 rounded bg-primary px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-opacity-90 disabled:opacity-50'
                            >
                                {payout.mip_sync_status === 'synced' ? 'Re-sync to MIP' : 'Sync to MIP'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* MIP Error Details Modal */}
            {errorDetailsData && (() => {
                const failedDocs = payout.mip_failed_documents || [];
                const errorByDoc = new Map<number, string>(
                    failedDocs.map((d) => [d.doc_number, d.error])
                );
                const failedDocNumbers = new Set(failedDocs.map((d) => d.doc_number));
                const failedRecords = errorDetailsData.records.filter((r) =>
                    failedDocNumbers.has(r.doc_number)
                );
                const visibleRecords = failedRecords.length > 0
                    ? failedRecords
                    : errorDetailsData.records;

                return (
                    <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4'>
                        <div className='max-h-[90vh] w-full max-w-7xl overflow-hidden rounded-sm bg-white shadow-lg dark:bg-boxdark'>
                            <div className='flex items-center justify-between border-b border-stroke px-6 py-4 dark:border-strokedark'>
                                <div>
                                    <h3 className='text-lg font-semibold text-danger'>
                                        MIP Sync Errors — Failed Records
                                    </h3>
                                    <p className='text-sm text-gray-500'>
                                        {failedDocs.length} document{failedDocs.length === 1 ? '' : 's'} rejected •{' '}
                                        {failedRecords.length} row{failedRecords.length === 1 ? '' : 's'} affected
                                    </p>
                                </div>
                                <button
                                    onClick={closeErrorDetails}
                                    className='rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-meta-4 dark:hover:text-white'
                                >
                                    <svg className='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                                        <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                                    </svg>
                                </button>
                            </div>

                            <div className='max-h-[calc(90vh-140px)] overflow-auto p-6'>
                                {visibleRecords.length === 0 ? (
                                    <div className='py-10 text-center text-gray-500'>
                                        No records available for this payout.
                                    </div>
                                ) : (
                                    <div className='overflow-x-auto'>
                                        <table className='w-full table-auto text-sm'>
                                            <thead>
                                                <tr className='border-b border-stroke bg-gray-50 dark:border-strokedark dark:bg-meta-4'>
                                                    <th className='px-3 py-2 text-left font-medium'>Date</th>
                                                    <th className='px-3 py-2 text-left font-medium'>Doc #</th>
                                                    <th className='px-3 py-2 text-left font-medium'>Fund ID</th>
                                                    <th className='px-3 py-2 text-left font-medium'>Gift Code</th>
                                                    <th className='px-3 py-2 text-left font-medium'>Type</th>
                                                    <th className='px-3 py-2 text-left font-medium'>GL Code</th>
                                                    <th className='px-3 py-2 text-left font-medium'>Cost Center</th>
                                                    <th className='px-3 py-2 text-left font-medium'>Program</th>
                                                    <th className='px-3 py-2 text-left font-medium'>FAS117</th>
                                                    <th className='px-3 py-2 text-left font-medium'>Region</th>
                                                    <th className='px-3 py-2 text-left font-medium'>Dept</th>
                                                    <th className='px-3 py-2 text-right font-medium'>Debit</th>
                                                    <th className='px-3 py-2 text-right font-medium'>Credit</th>
                                                    <th className='px-3 py-2 text-left font-medium'>Charge ID</th>
                                                    <th className='px-3 py-2 text-left font-medium'>Error</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {visibleRecords.map((record, idx) => {
                                                    const isFailed = failedDocNumbers.has(record.doc_number);
                                                    const error = errorByDoc.get(record.doc_number) || '';
                                                    return (
                                                        <tr
                                                            key={idx}
                                                            className={`border-b border-stroke dark:border-strokedark ${
                                                                isFailed ? 'bg-danger bg-opacity-10' : ''
                                                            }`}
                                                        >
                                                            <td className='px-3 py-2 whitespace-nowrap'>{record.date}</td>
                                                            <td className='px-3 py-2'>{record.doc_number}</td>
                                                            <td className='px-3 py-2'>{record.fund_id}</td>
                                                            <td className='px-3 py-2'>{record.gift_code}</td>
                                                            <td className='px-3 py-2'>{record.transaction_type}</td>
                                                            <td className='px-3 py-2'>{record.gl_code}</td>
                                                            <td className='px-3 py-2'>{record.cost_center}</td>
                                                            <td className='px-3 py-2'>{record.program_code}</td>
                                                            <td className='px-3 py-2'>{record.fas117}</td>
                                                            <td className='px-3 py-2'>{record.region}</td>
                                                            <td className='px-3 py-2'>{record.department}</td>
                                                            <td className='px-3 py-2 text-right font-mono'>
                                                                {record.debit_amount || ''}
                                                            </td>
                                                            <td className='px-3 py-2 text-right font-mono'>
                                                                {record.credit_amount || ''}
                                                            </td>
                                                            <td className='px-3 py-2'>
                                                                <code className='text-xs'>{record.charge_id}</code>
                                                            </td>
                                                            <td className='px-3 py-2 max-w-xs'>
                                                                {isFailed && (
                                                                    <span
                                                                        className='line-clamp-2 cursor-default text-danger'
                                                                        title={error}
                                                                    >
                                                                        {error}
                                                                    </span>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                            </div>

                            <div className='flex items-center justify-end gap-3 border-t border-stroke px-6 py-4 dark:border-strokedark'>
                                <button
                                    onClick={closeErrorDetails}
                                    className='rounded border border-stroke px-4 py-2 text-sm font-medium transition-colors hover:border-primary hover:text-primary dark:border-strokedark'
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Manual Payout Notice */}
            {manual_payout_notice && (
                <div className='mb-6 rounded-sm border border-warning bg-warning bg-opacity-10 px-4 py-4 text-black-2'>
                    <div className='flex items-start gap-3'>
                        <svg
                            className='h-5 w-5 flex-shrink-0 mt-0.5 text-warning'
                            fill='currentColor'
                            viewBox='0 0 20 20'
                        >
                            <path
                                fillRule='evenodd'
                                d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                                clipRule='evenodd'
                            />
                        </svg>
                        <div>
                            <div className='font-medium'>Manual Payout</div>
                            <div className='text-sm'>
                                {manual_payout_notice}{' '}
                                <a
                                    href='https://docs.stripe.com/reports/payout-reconciliation'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='underline hover:no-underline'
                                >
                                    Learn more
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Auto-Debit Payout Notice */}
            {auto_debit_notice && (
                <div className='mb-6 rounded-sm border border-warning bg-warning bg-opacity-10 px-4 py-4 text-black-2'>
                    <div className='flex items-start gap-3'>
                        <svg
                            className='h-5 w-5 flex-shrink-0 mt-0.5 text-warning'
                            fill='currentColor'
                            viewBox='0 0 20 20'
                        >
                            <path
                                fillRule='evenodd'
                                d='M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z'
                                clipRule='evenodd'
                            />
                        </svg>
                        <div>
                            <div className='font-medium'>Auto-Debit Payout</div>
                            <div className='text-sm'>
                                {auto_debit_notice}{' '}
                                <a
                                    href='https://docs.stripe.com/reports/payout-reconciliation'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className='underline hover:no-underline'
                                >
                                    Learn more
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Totals Summary - Only show for automatic payouts with transactions */}
            {!manual_payout_notice && !auto_debit_notice && (
            <div className='mb-6 grid grid-cols-2 gap-4 sm:grid-cols-5'>
                <div className='rounded-sm border border-stroke bg-white p-4 shadow-default dark:border-strokedark dark:bg-boxdark'>
                    <div className='text-sm text-gray-500'>Transactions</div>
                    <div className='text-xl font-bold text-black dark:text-white'>
                        {totals.count}
                    </div>
                </div>
                <div className='rounded-sm border border-stroke bg-white p-4 shadow-default dark:border-strokedark dark:bg-boxdark'>
                    <div className='text-sm text-gray-500'>Charges</div>
                    <div className='text-xl font-bold text-success'>
                        {formatCurrency(totals.charges, payout.currency)}
                    </div>
                </div>
                <div className='rounded-sm border border-stroke bg-white p-4 shadow-default dark:border-strokedark dark:bg-boxdark'>
                    <div className='text-sm text-gray-500'>Refunds</div>
                    <div className='text-xl font-bold text-danger'>
                        -{formatCurrency(totals.refunds, payout.currency)}
                    </div>
                </div>
                <div className='rounded-sm border border-stroke bg-white p-4 shadow-default dark:border-strokedark dark:bg-boxdark'>
                    <div className='text-sm text-gray-500'>Fees</div>
                    <div className='text-xl font-bold text-gray-600'>
                        -{formatCurrency(totals.fees, payout.currency)}
                    </div>
                </div>
                <div className='rounded-sm border border-stroke bg-white p-4 shadow-default dark:border-strokedark dark:bg-boxdark'>
                    <div className='text-sm text-gray-500'>Net</div>
                    <div className='text-xl font-bold text-primary'>
                        {formatCurrency(totals.net, payout.currency)}
                    </div>
                </div>
            </div>
            )}

            {/* MIP Errors Panel - errors from the last MIP sync attempt */}
            {hasPersistedMipErrors && (
                <section className='mb-6 rounded-sm border border-danger bg-danger bg-opacity-10 shadow-default'>
                    <div className='flex items-start justify-between gap-4 px-4 py-4 border-b border-danger border-opacity-30'>
                        <div>
                            <h3 className='font-semibold text-danger'>
                                MIP Sync Errors
                                {payout.mip_failed_documents && payout.mip_failed_documents.length > 0 && (
                                    <> ({payout.mip_failed_documents.length} document{payout.mip_failed_documents.length === 1 ? '' : 's'})</>
                                )}
                            </h3>
                            {payout.mip_error_message && (
                                <p className='mt-1 text-sm text-danger'>
                                    {payout.mip_error_message}
                                </p>
                            )}
                        </div>
                        {payout.mip_failed_documents && payout.mip_failed_documents.length > 0 && (
                            <button
                                onClick={handleViewErrorDetails}
                                disabled={errorDetailsLoading}
                                className='inline-flex flex-shrink-0 items-center gap-2 rounded border border-danger px-3 py-1.5 text-sm font-medium text-danger transition-colors hover:bg-danger hover:text-white disabled:opacity-50'
                            >
                                {errorDetailsLoading ? (
                                    <>
                                        <svg className='h-4 w-4 animate-spin' fill='none' viewBox='0 0 24 24'>
                                            <circle className='opacity-25' cx='12' cy='12' r='10' stroke='currentColor' strokeWidth='4'></circle>
                                            <path className='opacity-75' fill='currentColor' d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z'></path>
                                        </svg>
                                        Loading...
                                    </>
                                ) : (
                                    'View Details'
                                )}
                            </button>
                        )}
                    </div>
                    {errorDetailsError && (
                        <div className='border-b border-danger border-opacity-30 px-4 py-2 text-sm text-danger'>
                            {errorDetailsError}
                        </div>
                    )}
                    {payout.mip_failed_documents && payout.mip_failed_documents.length > 0 && (
                        <div className='overflow-x-auto'>
                            <table className='w-full table-auto text-sm'>
                                <thead>
                                    <tr className='border-b border-danger border-opacity-30'>
                                        <th className='px-4 py-2 text-left font-medium text-danger'>Doc #</th>
                                        <th className='px-4 py-2 text-left font-medium text-danger'>Document ID</th>
                                        <th className='px-4 py-2 text-left font-medium text-danger'>Error</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {payout.mip_failed_documents.map((doc) => (
                                        <tr
                                            key={doc.document_id}
                                            className='border-b border-danger border-opacity-20'
                                        >
                                            <td className='px-4 py-2'>{doc.doc_number}</td>
                                            <td className='px-4 py-2'>
                                                <code className='text-xs'>{doc.document_id}</code>
                                            </td>
                                            <td className='px-4 py-2 max-w-lg'>
                                                <span
                                                    className='line-clamp-2 cursor-default text-danger'
                                                    title={doc.error}
                                                >
                                                    {doc.error}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </section>
            )}

            {/* NXT Sync Records - Show when payout has been processed */}
            {payout.has_sync_record && chargeSyncs && chargeSyncs.charges.length > 0 && (
                <section className='mb-6 rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark'>
                    <div className='px-4 py-4 border-b border-stroke dark:border-strokedark'>
                        <div className='flex items-center justify-between'>
                            <h3 className='font-semibold text-black dark:text-white'>
                                NXT Gifts
                            </h3>
                            <div className='flex items-center gap-4 text-sm'>
                                <span className='text-success'>
                                    ✓ {chargeSyncs.summary.synced} synced
                                </span>
                                {chargeSyncs.summary.pending > 0 && (
                                    <span className='text-warning'>
                                        ⏳ {chargeSyncs.summary.pending} pending
                                    </span>
                                )}
                                {chargeSyncs.summary.failed > 0 && (
                                    <span className='text-danger'>
                                        ✗ {chargeSyncs.summary.failed} failed
                                    </span>
                                )}
                                {chargeSyncs.summary.skipped > 0 && (
                                    <span className='text-gray-500'>
                                        ⊘ {chargeSyncs.summary.skipped} skipped
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                    
                    {/* Reprocess error alert */}
                    {reprocessError && (
                        <div className='mx-4 mt-4 rounded-md bg-danger/10 px-4 py-3 text-sm text-danger'>
                            <div className='flex items-center justify-between'>
                                <span>Reprocess failed: {reprocessError}</span>
                                <button
                                    onClick={() => setReprocessError(null)}
                                    className='text-danger hover:text-danger/80'
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                    )}
                    
                    <div className='overflow-x-auto'>
                        <table className='w-full table-auto text-sm'>
                            <thead>
                                <tr className='border-b border-stroke bg-gray-50 dark:border-strokedark dark:bg-meta-4'>
                                    <th className='px-4 py-3 text-left font-medium'>Status</th>
                                    <th className='px-4 py-3 text-left font-medium'>Donor</th>
                                    <th className='px-4 py-3 text-left font-medium whitespace-nowrap'>In Honor Of</th>
                                    <th className='px-4 py-3 text-right font-medium'>Amount</th>
                                    <th className='px-4 py-3 text-right font-medium'>Tip</th>
                                    <th className='px-4 py-3 text-left font-medium'>Payment</th>
                                    <th className='px-4 py-3 text-left font-medium'>Gift Code</th>
                                    <th className='px-4 py-3 text-left font-medium'>NXT Gift</th>
                                    <th className='px-4 py-3 text-left font-medium'>Tip Gift</th>
                                    <th className='px-4 py-3 text-left font-medium'>Error</th>
                                    <th className='px-4 py-3 text-left font-medium'>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {chargeSyncs.charges.map((charge) => (
                                    <tr
                                        key={charge.charge_id}
                                        className='border-b border-stroke dark:border-strokedark'
                                    >
                                        <td className='px-4 py-3'>
                                            <span
                                                className={`inline-block rounded-full px-2 py-1 text-xs font-medium capitalize ${
                                                    charge.status === 'synced'
                                                        ? 'bg-success text-white'
                                                        : charge.status === 'pending'
                                                        ? 'bg-warning text-black'
                                                        : charge.status === 'failed'
                                                        ? 'bg-danger text-white'
                                                        : 'bg-gray-200 text-gray-600'
                                                }`}
                                            >
                                                {charge.status}
                                            </span>
                                        </td>
                                        <td className='px-4 py-3'>
                                            <div className='font-medium'>{charge.donor_name || 'Anonymous'}</div>
                                            <div className='mt-1 text-xs'>
                                                <a
                                                    href={buildStripePaymentUrl(charge.charge_id) || '#'}
                                                    target='_blank'
                                                    rel='noopener noreferrer'
                                                    className='text-primary hover:underline'
                                                >
                                                    Stripe transaction
                                                </a>
                                                <span className='text-gray-400 ml-1'>
                                                    ({formatCurrency(charge.amount)}{charge.fee_amount > 0 && (
                                                        <>, {formatCurrency(charge.fee_amount)} fee{charge.tip_amount > 0 ? ' — covered by tip' : ''}</>
                                                    )})
                                                </span>
                                            </div>
                                        </td>
                                        <td className='px-4 py-3'>
                                            {charge.campaign_name || charge.patient_name || charge.ntaf_id ? (
                                                <>
                                                    <div className='font-medium whitespace-nowrap'>
                                                        {charge.campaign_id ? (
                                                            <Link
                                                                to={`/campaigns/${charge.campaign_id}`}
                                                                className='text-primary hover:underline'
                                                            >
                                                                {charge.patient_name || charge.campaign_name}
                                                            </Link>
                                                        ) : (
                                                            // Unrestricted gifts have no campaign; they are
                                                            // booked to the general fund under NTAF.
                                                            charge.patient_name ||
                                                            charge.campaign_name ||
                                                            (charge.is_restricted ? '' : 'General Fund')
                                                        )}
                                                    </div>
                                                    {charge.ntaf_id && (
                                                        <div className='text-xs text-gray-500'>
                                                            NTAF: <code>{charge.ntaf_id}</code>
                                                        </div>
                                                    )}
                                                    {charge.fund_id && (
                                                        <div className='text-xs text-gray-500'>
                                                            Fund:{' '}
                                                            <a
                                                                href={nxtFundUrl(charge.fund_id)}
                                                                target='_blank'
                                                                rel='noopener noreferrer'
                                                                className='text-primary hover:underline'
                                                            >
                                                                {charge.fund_id}
                                                            </a>
                                                        </div>
                                                    )}
                                                </>
                                            ) : (
                                                <span className='text-gray-400'>—</span>
                                            )}
                                        </td>
                                        <td className='px-4 py-3 text-right font-medium'>
                                            {formatCurrency(charge.donation_amount)}
                                        </td>
                                        <td className='px-4 py-3 text-right'>
                                            {charge.tip_amount > 0 ? formatCurrency(charge.tip_amount) : ''}
                                        </td>
                                        <td className='px-4 py-3 text-xs capitalize'>
                                            {charge.payment_method && charge.payment_method !== 'unknown'
                                                ? charge.payment_method.replace('_', ' ')
                                                : ''}
                                            {charge.card_brand && charge.card_brand !== charge.payment_method && (
                                                <span className='block text-gray-400'>
                                                    {charge.card_brand}
                                                </span>
                                            )}
                                        </td>
                                        <td className='px-4 py-3'>
                                            <code className='text-xs'>{charge.gift_code}</code>
                                        </td>
                                        <td className='px-4 py-3'>
                                            {charge.nxt_gift_id ? (
                                                <span className='inline-flex items-center gap-1'>
                                                    {charge.primary_loc_id ? (
                                                        <button
                                                            type='button'
                                                            onClick={() =>
                                                                openDonationDrawer(
                                                                    charge.primary_loc_id as number
                                                                )
                                                            }
                                                            disabled={
                                                                openingLocId ===
                                                                charge.primary_loc_id
                                                            }
                                                            className='text-primary hover:underline disabled:opacity-50'
                                                            title='View donation detail'
                                                        >
                                                            {charge.nxt_gift_id}
                                                        </button>
                                                    ) : (
                                                        <span>{charge.nxt_gift_id}</span>
                                                    )}
                                                    <a
                                                        href={nxtGiftUrl(charge.nxt_gift_id)}
                                                        target='_blank'
                                                        rel='noopener noreferrer'
                                                        className='text-primary hover:underline'
                                                        title='Open in NXT'
                                                    >
                                                        <svg
                                                            className='h-3 w-3'
                                                            fill='none'
                                                            viewBox='0 0 24 24'
                                                            stroke='currentColor'
                                                        >
                                                            <path
                                                                strokeLinecap='round'
                                                                strokeLinejoin='round'
                                                                strokeWidth={2}
                                                                d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
                                                            />
                                                        </svg>
                                                    </a>
                                                </span>
                                            ) : (
                                                <span className='text-gray-400'>—</span>
                                            )}
                                        </td>
                                        <td className='px-4 py-3'>
                                            {charge.nxt_tip_gift_id ? (
                                                <span className='inline-flex items-center gap-1'>
                                                    {charge.tip_loc_id ? (
                                                        <button
                                                            type='button'
                                                            onClick={() =>
                                                                openDonationDrawer(
                                                                    charge.tip_loc_id as number
                                                                )
                                                            }
                                                            disabled={
                                                                openingLocId ===
                                                                charge.tip_loc_id
                                                            }
                                                            className='text-primary hover:underline disabled:opacity-50'
                                                            title='View donation detail'
                                                        >
                                                            {charge.nxt_tip_gift_id}
                                                        </button>
                                                    ) : (
                                                        <span>{charge.nxt_tip_gift_id}</span>
                                                    )}
                                                    <a
                                                        href={nxtGiftUrl(charge.nxt_tip_gift_id)}
                                                        target='_blank'
                                                        rel='noopener noreferrer'
                                                        className='text-primary hover:underline'
                                                        title='Open in NXT'
                                                    >
                                                        <svg
                                                            className='h-3 w-3'
                                                            fill='none'
                                                            viewBox='0 0 24 24'
                                                            stroke='currentColor'
                                                        >
                                                            <path
                                                                strokeLinecap='round'
                                                                strokeLinejoin='round'
                                                                strokeWidth={2}
                                                                d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
                                                            />
                                                        </svg>
                                                    </a>
                                                </span>
                                            ) : (
                                                <span className='text-gray-400'>—</span>
                                            )}
                                        </td>
                                        <td className='px-4 py-3 text-xs text-danger max-w-sm'>
                                            <span
                                                className='line-clamp-2 cursor-default'
                                                title={charge.error_message || undefined}
                                            >
                                                {charge.error_message}
                                            </span>
                                        </td>
                                        <td className='px-4 py-3'>
                                            <div className='flex items-center gap-2'>
                                            {charge.status === 'synced' && (
                                                <button
                                                    onClick={() => handleChargePreview(charge.charge_id)}
                                                    disabled={previewingChargeId === charge.charge_id}
                                                    className='inline-flex items-center gap-1 whitespace-nowrap rounded border border-stroke px-3 py-1.5 text-xs font-medium text-gray-600 hover:border-primary hover:text-primary disabled:opacity-50 dark:border-strokedark dark:text-gray-400 dark:hover:border-primary dark:hover:text-primary'
                                                    title='Preview the MIP accounting payload for this gift'
                                                >
                                                    {previewingChargeId === charge.charge_id ? (
                                                        <>
                                                            <svg
                                                                className='h-3 w-3 animate-spin'
                                                                fill='none'
                                                                viewBox='0 0 24 24'
                                                            >
                                                                <circle
                                                                    className='opacity-25'
                                                                    cx='12'
                                                                    cy='12'
                                                                    r='10'
                                                                    stroke='currentColor'
                                                                    strokeWidth='4'
                                                                />
                                                                <path
                                                                    className='opacity-75'
                                                                    fill='currentColor'
                                                                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z'
                                                                />
                                                            </svg>
                                                            Loading...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <svg
                                                                className='h-3 w-3'
                                                                fill='none'
                                                                viewBox='0 0 24 24'
                                                                stroke='currentColor'
                                                            >
                                                                <path
                                                                    strokeLinecap='round'
                                                                    strokeLinejoin='round'
                                                                    strokeWidth={2}
                                                                    d='M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
                                                                />
                                                            </svg>
                                                            Preview MIP
                                                        </>
                                                    )}
                                                </button>
                                            )}
                                            {(charge.status === 'synced' || charge.status === 'failed') && (
                                                <button
                                                    onClick={() => handleReprocessCharge(charge.charge_id)}
                                                    disabled={reprocessingCharge === charge.charge_id}
                                                    className='inline-flex items-center gap-1 whitespace-nowrap rounded bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-opacity-90 disabled:opacity-50'
                                                    title='Delete the existing NXT gift(s) and recreate them with current data (fixes wrong gift dates)'
                                                >
                                                    {reprocessingCharge === charge.charge_id ? (
                                                        <>
                                                            <svg
                                                                className='h-3 w-3 animate-spin'
                                                                fill='none'
                                                                viewBox='0 0 24 24'
                                                            >
                                                                <circle
                                                                    className='opacity-25'
                                                                    cx='12'
                                                                    cy='12'
                                                                    r='10'
                                                                    stroke='currentColor'
                                                                    strokeWidth='4'
                                                                />
                                                                <path
                                                                    className='opacity-75'
                                                                    fill='currentColor'
                                                                    d='M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z'
                                                                />
                                                            </svg>
                                                            Re-syncing...
                                                        </>
                                                    ) : (
                                                        <>
                                                            <svg
                                                                className='h-3 w-3'
                                                                fill='none'
                                                                viewBox='0 0 24 24'
                                                                stroke='currentColor'
                                                            >
                                                                <path
                                                                    strokeLinecap='round'
                                                                    strokeLinejoin='round'
                                                                    strokeWidth={2}
                                                                    d='M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15'
                                                                />
                                                            </svg>
                                                            Re-sync to NXT
                                                        </>
                                                    )}
                                                </button>
                                            )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </section>
            )}

            {/* Per-Charge MIP Preview Modal */}
            {chargeMipPreview && (
                <div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4'>
                    <div className='max-h-[90vh] w-full max-w-4xl overflow-hidden rounded-sm bg-white shadow-lg dark:bg-boxdark'>
                        <div className='flex items-center justify-between border-b border-stroke px-6 py-4 dark:border-strokedark'>
                            <div>
                                <h3 className='text-lg font-semibold text-black dark:text-white'>
                                    MIP Preview — Charge
                                </h3>
                                <p className='text-sm text-gray-500'>
                                    <code>{chargeMipPreview.charge_id}</code> — {chargeMipPreview.total_records} records
                                </p>
                            </div>
                            <button
                                onClick={closeChargePreview}
                                className='rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-meta-4 dark:hover:text-white'
                            >
                                <svg className='h-6 w-6' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                                    <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M6 18L18 6M6 6l12 12' />
                                </svg>
                            </button>
                        </div>

                        <div className='max-h-[calc(90vh-140px)] overflow-auto p-6'>
                            {chargeMipPreview.records.length === 0 ? (
                                <div className='py-10 text-center text-gray-500'>
                                    {chargeMipPreview.errors.length > 0
                                        ? chargeMipPreview.errors.join(', ')
                                        : 'No MIP records for this charge.'}
                                </div>
                            ) : (
                                <div className='overflow-x-auto'>
                                    <table className='w-full table-auto text-sm'>
                                        <thead>
                                            <tr className='border-b border-stroke bg-gray-50 dark:border-strokedark dark:bg-meta-4'>
                                                <th className='px-3 py-2 text-left font-medium'>Date</th>
                                                <th className='px-3 py-2 text-left font-medium'>Batch ID</th>
                                                <th className='px-3 py-2 text-left font-medium'>Doc #</th>
                                                <th className='px-3 py-2 text-left font-medium'>Fund ID</th>
                                                <th className='px-3 py-2 text-left font-medium'>Gift Code</th>
                                                <th className='px-3 py-2 text-left font-medium'>Type</th>
                                                <th className='px-3 py-2 text-left font-medium'>GL Code</th>
                                                <th className='px-3 py-2 text-left font-medium'>Cost Center</th>
                                                <th className='px-3 py-2 text-left font-medium'>Program</th>
                                                <th className='px-3 py-2 text-left font-medium'>FAS117</th>
                                                <th className='px-3 py-2 text-left font-medium'>Region</th>
                                                <th className='px-3 py-2 text-left font-medium'>Dept</th>
                                                <th className='px-3 py-2 text-right font-medium'>Debit</th>
                                                <th className='px-3 py-2 text-right font-medium'>Credit</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {chargeMipPreview.records.map((record, idx) => (
                                                <tr
                                                    key={idx}
                                                    className='border-b border-stroke dark:border-strokedark'
                                                >
                                                    <td className='px-3 py-2 whitespace-nowrap'>{record.date}</td>
                                                    <td className='px-3 py-2'>
                                                        <code className='text-xs'>{record.batch_id}</code>
                                                    </td>
                                                    <td className='px-3 py-2'>{record.doc_number}</td>
                                                    <td className='px-3 py-2'>{record.fund_id}</td>
                                                    <td className='px-3 py-2'>{record.gift_code}</td>
                                                    <td className='px-3 py-2'>{record.transaction_type}</td>
                                                    <td className='px-3 py-2'>{record.gl_code}</td>
                                                    <td className='px-3 py-2'>{record.cost_center}</td>
                                                    <td className='px-3 py-2'>{record.program_code}</td>
                                                    <td className='px-3 py-2'>{record.fas117}</td>
                                                    <td className='px-3 py-2'>{record.region}</td>
                                                    <td className='px-3 py-2'>{record.department}</td>
                                                    <td className='px-3 py-2 text-right font-mono'>
                                                        {record.debit_amount || ''}
                                                    </td>
                                                    <td className='px-3 py-2 text-right font-mono'>
                                                        {record.credit_amount || ''}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>

                        <div className='flex items-center justify-end gap-3 border-t border-stroke px-6 py-4 dark:border-strokedark'>
                            {chargeMipPreview.records.length > 0 && (
                                <button
                                    onClick={() => downloadCsv(
                                        chargeMipPreview.records,
                                        `mip-preview-${chargeMipPreview.charge_id}.csv`
                                    )}
                                    className='inline-flex items-center gap-2 rounded border border-stroke px-4 py-2 text-sm font-medium transition-colors hover:border-primary hover:text-primary dark:border-strokedark'
                                >
                                    Download CSV
                                </button>
                            )}
                            <button
                                onClick={closeChargePreview}
                                className='rounded border border-stroke px-4 py-2 text-sm font-medium transition-colors hover:border-primary hover:text-primary dark:border-strokedark'
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Loading state for charge syncs */}
            {payout.has_sync_record && loadingChargeSyncs && (
                <div className='mb-6 flex items-center justify-center py-10'>
                    <div className='h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent'></div>
                </div>
            )}

            {/* Transactions Table - Only show for automatic payouts */}
            {!manual_payout_notice && !auto_debit_notice && (
            <section className='data-table-common rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark'>
                <div className='px-4 py-4 border-b border-stroke dark:border-strokedark'>
                    <h3 className='font-semibold text-black dark:text-white'>
                        Balance Transactions
                    </h3>
                </div>

                {transactions.length === 0 ? (
                    <div className='py-10 text-center text-gray-500'>
                        No transactions found for this payout.
                    </div>
                ) : (
                    <>
                        <div className='overflow-x-auto'>
                            <table className='datatable-table datatable-one w-full table-auto border-collapse overflow-hidden break-words px-4 md:overflow-auto md:px-8'>
                                <thead className='border-separate px-4'>
                                    <tr
                                        className='border-t border-stroke bg-whiten dark:bg-meta-4'
                                        style={{ fontSize: '14px' }}
                                    >
                                        <th className='w-20 px-4 py-4 text-left font-medium'>Type</th>
                                        <th className='px-4 py-4 text-left font-medium'>
                                            Source ID
                                        </th>
                                        <th className='w-28 px-4 py-4 text-right font-medium'>
                                            Amount
                                        </th>
                                        <th className='w-24 px-4 py-4 text-right font-medium'>Fee</th>
                                        <th className='w-28 px-4 py-4 text-right font-medium'>Net</th>
                                        <th className='px-4 py-4 text-left font-medium'>
                                            Created
                                        </th>
                                        <th className='px-4 py-4 text-left font-medium'>
                                            Description
                                        </th>
                                        <th className='w-40 px-4 py-4 text-left font-medium'>
                                            Status
                                        </th>
                                        <th className='w-32 px-4 py-4 text-left font-medium'>
                                            Metadata
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transactions.map((txn: StripeBalanceTransaction) => (
                                        <tr
                                            key={txn.id}
                                            className='border-t border-stroke dark:border-strokedark'
                                        >
                                            <td className='px-4 py-4'>
                                                <span
                                                    className={`inline-block rounded-full px-3 py-1 text-xs font-medium capitalize ${
                                                        txnTypeColors[txn.type] ||
                                                        'bg-gray-200 text-gray-700'
                                                    }`}
                                                >
                                                    {txn.type.replace('_', ' ')}
                                                </span>
                                            </td>
                                            <td className='px-4 py-4'>
                                                {txn.source && (
                                                    <a
                                                        href={buildStripePaymentUrl(txn.source) || '#'}
                                                        target='_blank'
                                                        rel='noopener noreferrer'
                                                        className='inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-1 text-xs transition-colors hover:bg-primary hover:text-white dark:bg-meta-4 dark:hover:bg-primary'
                                                    >
                                                        <code>{txn.source}</code>
                                                        <svg
                                                            className='h-3 w-3'
                                                            fill='none'
                                                            viewBox='0 0 24 24'
                                                            stroke='currentColor'
                                                        >
                                                            <path
                                                                strokeLinecap='round'
                                                                strokeLinejoin='round'
                                                                strokeWidth={2}
                                                                d='M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14'
                                                            />
                                                        </svg>
                                                    </a>
                                                )}
                                            </td>
                                            <td
                                                className={`px-4 py-4 text-right font-medium ${
                                                    txn.amount < 0
                                                        ? 'text-danger'
                                                        : 'text-success'
                                                }`}
                                            >
                                                {formatCurrency(txn.amount, txn.currency)}
                                            </td>
                                            <td className='px-4 py-4 text-right text-gray-500'>
                                                {txn.fee > 0
                                                    ? `-${formatCurrency(txn.fee, txn.currency)}`
                                                    : ''}
                                            </td>
                                            <td
                                                className={`px-4 py-4 text-right font-medium ${
                                                    txn.net < 0 ? 'text-danger' : ''
                                                }`}
                                            >
                                                {formatCurrency(txn.net, txn.currency)}
                                            </td>
                                            <td className='px-4 py-4 text-sm'>
                                                {formatDateTime(txn.created)}
                                            </td>
                                            <td className='px-4 py-4 text-sm text-gray-500'>
                                                {txn.description || ''}
                                            </td>
                                            <td className='px-4 py-4 text-sm'>
                                                {/* Manual RE + MIP completion for refund rows */}
                                                {txn.type === 'refund' && txn.completion_status && txn.refund_id ? (
                                                    <div className='flex flex-col items-start gap-1'>
                                                        <span
                                                            className={`inline-block rounded-full px-3 py-1 text-xs font-medium capitalize ${
                                                                txn.completion_status === 'complete'
                                                                    ? 'bg-success text-white'
                                                                    : 'bg-warning text-black'
                                                            }`}
                                                        >
                                                            {txn.completion_status}
                                                        </span>
                                                        {txn.completion_status === 'complete' ? (
                                                            <>
                                                                {txn.completed_at && (
                                                                    <span className='text-xs text-gray-400'>
                                                                        {formatDate(txn.completed_at)}
                                                                    </span>
                                                                )}
                                                                <button
                                                                    onClick={() =>
                                                                        handleMarkRefundComplete(txn.refund_id!, 'pending')
                                                                    }
                                                                    disabled={markingRefund === txn.refund_id}
                                                                    className='text-xs text-gray-500 underline transition-colors hover:text-primary disabled:opacity-50'
                                                                >
                                                                    {markingRefund === txn.refund_id ? 'Saving…' : 'Reopen'}
                                                                </button>
                                                            </>
                                                        ) : (
                                                            <button
                                                                onClick={() =>
                                                                    handleMarkRefundComplete(txn.refund_id!, 'complete')
                                                                }
                                                                disabled={markingRefund === txn.refund_id}
                                                                className='inline-flex items-center gap-1 rounded border border-stroke px-2 py-1 text-xs font-medium text-gray-600 transition-colors hover:border-primary hover:text-primary disabled:opacity-50 dark:border-strokedark dark:text-gray-400'
                                                            >
                                                                {markingRefund === txn.refund_id ? 'Saving…' : 'Mark Complete'}
                                                            </button>
                                                        )}
                                                    </div>
                                                ) : (
                                                    ''
                                                )}
                                            </td>
                                            <td className='px-4 py-4 text-sm'>
                                                {/* Only show metadata button for charges/payments */}
                                                {(txn.type === 'charge' || txn.type === 'payment') && txn.source ? (
                                                    <>
                                                        {/* Not yet fetched - show button */}
                                                        {!(txn.source in chargeMetadata) && (
                                                            <button
                                                                onClick={() => handleFetchMetadata(txn.source!)}
                                                                disabled={loadingMetadata[txn.source]}
                                                                className='inline-flex items-center gap-1 rounded border border-stroke px-2 py-1 text-xs font-medium text-gray-600 transition-colors hover:border-primary hover:text-primary disabled:opacity-50 dark:border-strokedark dark:text-gray-400'
                                                            >
                                                                {loadingMetadata[txn.source] ? (
                                                                    <>
                                                                        <div className='h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent'></div>
                                                                        Loading...
                                                                    </>
                                                                ) : (
                                                                    'Show Metadata'
                                                                )}
                                                            </button>
                                                        )}
                                                        {/* Fetched - show metadata or "No metadata" */}
                                                        {txn.source in chargeMetadata && (
                                                            chargeMetadata[txn.source] && Object.keys(chargeMetadata[txn.source]!).length > 0 ? (
                                                                <div className='space-y-1'>
                                                                    {Object.entries(chargeMetadata[txn.source]!).map(([key, value]) => (
                                                                        <div key={key} className='flex gap-1'>
                                                                            <span className='font-medium text-gray-600 dark:text-gray-400'>
                                                                                {key}:
                                                                            </span>
                                                                            {/* gf_entry_url / gf_source_url point back at the
                                                                                WordPress entry that made the gift — the whole
                                                                                point of stamping them is being able to click
                                                                                through from the charge. */}
                                                                            {/^https?:\/\//.test(value) ? (
                                                                                <a
                                                                                    href={value}
                                                                                    target='_blank'
                                                                                    rel='noopener noreferrer'
                                                                                    className='break-all text-primary underline'
                                                                                >
                                                                                    {value}
                                                                                </a>
                                                                            ) : (
                                                                                <span className='text-gray-500'>
                                                                                    {value}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <span className='text-xs text-gray-400'>No metadata</span>
                                                            )
                                                        )}
                                                    </>
                                                ) : (
                                                    ''
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {has_more && (
                            <div className='flex justify-center border-t border-stroke px-4 py-4 dark:border-strokedark'>
                                <button
                                    onClick={handleLoadMore}
                                    disabled={loadingMore}
                                    className='inline-flex items-center justify-center gap-2 rounded-full border border-primary px-6 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary hover:text-white disabled:opacity-50'
                                >
                                    {loadingMore ? (
                                        <>
                                            <div className='h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent'></div>
                                            Loading...
                                        </>
                                    ) : (
                                        'Load More Transactions'
                                    )}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </section>
            )}

            <DonationDetailDrawer
                isOpen={activeDonation !== null}
                setIsOpen={(open) => {
                    if (!open) setActiveDonation(null);
                }}
                donation={activeDonation}
            />
        </>
    );
};

export default PayoutDetail;
